// src/features/leases/leaseRisk.js
//
// SCORING-LEASE-WIRE: Client-side lease risk computation that mirrors the
// server scoreLeaseRisk sub-model. Display-only — the server remains
// authoritative for the Location Score.
//
// Weighted factors (3-factor model):
//   Time-to-expiry   65%  (>7yr→100, 5-7→90, 3-5→75, 2-3→55, 1-2→35, <1→15, expired→0)
//   Lease type       20%  (direct_lease→80, sublease_inbound→60, outbound→50, missing→50)
//   Optionality      15%  (additive rubric: renewal +30, break +12/25, expansion/ROFR +20,
//                          contraction +15, no sublet recapture +10; cap 100)
//
// No-imputation rule: when all five structured option fields (break_is_mutual,
// has_expansion, has_rofr, has_contraction, sublet_recapture_right) are NULL
// AND no classic renewal info exists, Optionality is excluded from the weighted
// sum and Time + Lease type renormalize to fill the 100% envelope.
//
// Serviced-office leases are scored on flex availability, not expiry runway.
//
// Risk bands:
//   ≥ 80  → "Low"      (green)
//   55–79 → "Moderate" (amber)
//   < 55  → "High"     (red)

import { T } from "../../app/tokens.js";
import { parseDateOnly } from "./leaseTabConfig.js";

// ─── Constants ────────────────────────────────────────────────────────────────

export const RISK_BANDS = Object.freeze({
  low:      { label: "Low Risk",      min: 80,  color: "low"      },
  moderate: { label: "Moderate Risk", min: 55,  color: "moderate" },
  high:     { label: "High Risk",     min: 0,   color: "high"     },
});

// ─── Individual factor helpers ────────────────────────────────────────────────

/** Time-to-expiry sub-factor score (0–100) using the stepped curve. */
export function expiryFactorScore(leaseEndDate) {
  if (!leaseEndDate) return { score: 50, missing: true };
  const end = parseDateOnly(leaseEndDate);
  if (!end) return { score: 50, missing: true };

  const msLeft = end.getTime() - Date.now();
  if (msLeft <= 0) return { score: 0, missing: false, expired: true };

  const yearsLeft = msLeft / (1000 * 60 * 60 * 24 * 365.25);
  let score;
  if      (yearsLeft > 7) score = 100;
  else if (yearsLeft > 5) score = 90;
  else if (yearsLeft > 3) score = 75;
  else if (yearsLeft > 2) score = 55;
  else if (yearsLeft > 1) score = 35;
  else                    score = 15;

  return { score, missing: false, yearsLeft };
}

/**
 * optionalityFactorScore(opts)
 *
 * Additive rubric (starts at 0, cap 100):
 *   +30  renewal option or renewalCount > 0
 *   +25  live break clause, not mutual (break_is_mutual is NOT true)
 *   +12  live break clause, mutual (break_is_mutual === true)
 *   +20  live expansion option OR ROFR on additional space
 *   +15  contraction right
 *   +10  sublet recapture right explicitly waived (=== false)
 *
 * Excluded (returns { score: 0, excluded: true }) when all structured option
 * fields are NULL and no classic renewal info is present. The caller should
 * then renormalize Time + Lease type weights to fill the 100% envelope.
 */
export function optionalityFactorScore(opts = {}) {
  const {
    renewalOption        = false,
    renewalCount         = 0,
    breakDate            = null,
    breakIsMutual        = null,
    hasExpansion         = null,
    expansionDeadline    = null,
    hasRofr              = null,
    hasContraction       = null,
    subletRecaptureRight = null,
  } = opts;

  const excluded = (
    breakIsMutual     == null &&
    hasExpansion      == null &&
    hasRofr           == null &&
    hasContraction    == null &&
    !renewalOption    &&
    Number(renewalCount) === 0
  );

  if (excluded) return { score: 0, excluded: true };

  let score = 0;
  if (renewalOption || Number(renewalCount) > 0) score += 30;

  if (breakDate) {
    const bd = parseDateOnly(breakDate);
    if (bd && bd.getTime() > Date.now()) {
      score += (breakIsMutual === true) ? 12 : 25;
    }
  }

  if (
    (hasExpansion && (!expansionDeadline || parseDateOnly(expansionDeadline) > new Date())) ||
    hasRofr
  ) {
    score += 20;
  }

  if (hasContraction) score += 15;
  if (subletRecaptureRight === false) score += 10;

  score = Math.min(100, score);
  return { score, excluded: false };
}

/** Lease type/security sub-factor score (0–100). */
export function leaseTypeFactorScore(leaseType) {
  if (leaseType === "direct_lease")      return { score: 80, missing: false };
  if (leaseType === "sublease_inbound")  return { score: 60, missing: false };
  if (leaseType === "sublease_outbound") return { score: 50, missing: false };
  return { score: 50, missing: !leaseType };
}

// ─── Top-driver sentence ──────────────────────────────────────────────────────

/** Build a concise one-line sentence naming the primary risk driver. */
export function topDriverSentence(leaseType, expiryResult, optResult, typeResult) {
  if (leaseType === "serviced_office") {
    return "Serviced office - flex contract with month-to-month exposure.";
  }

  // When optionality is excluded treat it as neutral (50) so it doesn't
  // dominate the shortfall ranking.
  const optEffScore = optResult.excluded ? 50 : optResult.score;
  const factors = [
    { name: "expiry",      score: expiryResult.score, weight: 0.65 },
    { name: "optionality", score: optEffScore,         weight: 0.15 },
    { name: "type",        score: typeResult.score,    weight: 0.20 },
  ];

  const shortfalls = factors.map((f) => ({ ...f, shortfall: (100 - f.score) * f.weight }));
  shortfalls.sort((a, b) => b.shortfall - a.shortfall);
  const primary = shortfalls[0].name;

  if (primary === "expiry") {
    if (expiryResult.expired) return "Lease has expired or is in holdover.";
    if (expiryResult.yearsLeft != null) {
      const yrs = expiryResult.yearsLeft;
      if (yrs < 1) return "Expires in under a year - critical runway.";
      if (yrs < 2) return `Expires in ~${Math.round(yrs * 12)} months - limited runway.`;
      if (yrs < 3) return `Expires in ~${Math.round(yrs)} years - watch renewal window.`;
      return `${Math.round(yrs * 10) / 10} years remaining on lease.`;
    }
    return "Lease expiry date not set.";
  }
  if (primary === "optionality") {
    if (optResult.excluded) return "Options not yet abstracted - no optionality score.";
    if (optResult.score < 30) return "No options on record - limited flexibility.";
    return "Limited contractual options reduce long-term certainty.";
  }
  if (primary === "type") {
    if (typeResult.missing) return "Lease type not recorded.";
    return "Sublease structure adds sub-tenant risk.";
  }
  return "Risk driven by multiple factors.";
}

// ─── Countdown color utility ──────────────────────────────────────────────────

/**
 * getCountdownStyle(months)
 *
 * Returns { color, fontWeight } for a month countdown label using a three-tier
 * threshold rule:
 *   1–6 months  → red    (T.ragRed),       weight T.fwSemibold
 *   7–12 months → amber  (T.ragAmber),     weight T.fwSemibold
 *   13+ months  → muted  (T.light),        weight T.fwRegular
 */
export function getCountdownStyle(months) {
  if (months <= 6)  return { color: T.ragRed,    fontWeight: T.fwSemibold };
  if (months <= 12) return { color: T.ragAmber,  fontWeight: T.fwSemibold };
  return              { color: T.light,         fontWeight: T.fwRegular  };
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * computeLeaseRisk(lease)
 *
 * Accepts a lease object (as returned by the leases API or listLocationLeases)
 * and returns a risk assessment for display in the LeaseHeroBand.
 *
 * Returns:
 *   { score: number, band: 'low'|'moderate'|'high', bandWord: string,
 *     topDriver: string, partial: boolean }
 */
export function computeLeaseRisk(lease) {
  if (!lease) {
    return { score: 50, band: "moderate", bandWord: "Moderate Risk", topDriver: "No lease data.", partial: true };
  }

  // Normalise snake_case (detail objects) and camelCase (list-row objects).
  // List rows from the leases API expose `leaseType`; detail objects from
  // getLeaseById expose `type`.  Support both so every call site is covered
  // without needing a per-caller spread.
  const leaseType = lease.type || lease.leaseType;

  // Serviced-office: flex path (expiry curve does not apply)
  if (leaseType === "serviced_office") {
    const hasOptions = lease.renewalOption || Number(lease.renewalCount) > 0;
    const score = hasOptions ? 75 : 65;
    return {
      score,
      band:     score >= 80 ? "low" : score >= 55 ? "moderate" : "high",
      bandWord: score >= 80 ? "Low Risk" : score >= 55 ? "Moderate Risk" : "High Risk",
      topDriver: "Serviced office - flex contract with month-to-month exposure.",
      partial: false,
    };
  }

  const expiryResult = expiryFactorScore(lease.endDate);
  const optResult    = optionalityFactorScore({
    renewalOption:       lease.renewalOption,
    renewalCount:        lease.renewalCount,
    breakDate:           lease.breakDate,
    breakIsMutual:       lease.breakIsMutual,
    hasExpansion:        lease.hasExpansion,
    expansionDeadline:   lease.expansionDeadline,
    hasRofr:             lease.hasRofr,
    hasContraction:      lease.hasContraction,
    subletRecaptureRight: lease.subletRecaptureRight,
  });
  const typeResult   = leaseTypeFactorScore(leaseType);

  // Renormalize when optionality is excluded.
  let rawScore;
  if (optResult.excluded) {
    rawScore = expiryResult.score * (65 / 85) + typeResult.score * (20 / 85);
  } else {
    rawScore =
      expiryResult.score * 0.65 +
      optResult.score    * 0.15 +
      typeResult.score   * 0.20;
  }

  const score    = Math.round(rawScore * 10) / 10;
  const band     = score >= 80 ? "low" : score >= 55 ? "moderate" : "high";
  const bandWord = score >= 80 ? "Low Risk" : score >= 55 ? "Moderate Risk" : "High Risk";
  const partial  = expiryResult.missing || typeResult.missing;

  const topDriver = topDriverSentence(leaseType, expiryResult, optResult, typeResult);

  return { score, band, bandWord, topDriver, partial };
}
